import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  ParsedExcelData,
  ValidationError
} from '@/lib/excel';

const HEADERS = {
  DOC_NUMBER: 'Số c/từ*',
  DOC_DATE: 'Ngày chứng từ*',
  CURRENCY: 'Ngoại tệ*',
  EXCHANGE_RATE: 'Tỷ giá',
  ADDRESS: 'Địa chỉ',
  RECIPIENT: 'Người nhận tiền',
  DESCRIPTION: 'Diễn giải',
  CREDIT_ACCOUNT: 'Tài khoản có*',
  CUSTOMER_CODE: 'Mã khách hàng*',
  CUSTOMER_NAME: 'Tên khách hàng',
  DEBIT_ACCOUNT: 'Tài khoản nợ',
  BANK: 'Ngân hàng',
  AMOUNT_NT: 'Tiền nt',
  AMOUNT: 'Tiền',
  DETAIL_DESC: 'Diễn giải chi tiết',
  LOAI_HD: 'Loại hóa đơn',
  MA_THUE: 'Mã thuế',
  TK_THUE: 'Tk thuế',
  SO_HD: 'Số hóa đơn',
  KY_HIEU: 'Ký hiệu',
  NGAY_HD: 'Ngày hóa đơn',
  MAU_HD: 'Mẫu HĐ',
  MAU_BC: 'Mẫu báo cáo',
  MA_TC: 'Mã tính chất',
  MA_NCC: 'Mã ncc',
  TEN_NCC: 'Tên nhà cung cấp',
  MA_SO_THUE: 'Mã số thuế',
  TEN_VT_THUE: 'Tên hàng hóa - dịch vụ',
  THUE_NT: 'Thuế nt',
  THUE: 'Thuế',
  CUC_THUE: 'Cục thuế',
  GHI_CHU: 'Ghi chú',
  DEPARTMENT: 'Bộ phận',
  FEE: 'Phí'
};

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.DOC_NUMBER, key: 'so_ct', type: 'main' },
  { header: HEADERS.DOC_DATE, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.CURRENCY, key: 'ma_nt', type: 'main' },
  { header: HEADERS.EXCHANGE_RATE, key: 'ty_gia', type: 'main' },
  { header: HEADERS.ADDRESS, key: 'dia_chi', type: 'main' },
  { header: HEADERS.RECIPIENT, key: 'ong_ba', type: 'main' },
  { header: HEADERS.DESCRIPTION, key: 'dien_giai', type: 'main' },
  { header: HEADERS.CREDIT_ACCOUNT, key: 'tk', type: 'main' },
  { header: HEADERS.BANK, key: 'tknh', type: 'main' },
  { header: HEADERS.LOAI_HD, key: 'loai_hd', type: 'main' },

  // Detail fields
  { header: HEADERS.CUSTOMER_CODE, key: 'ma_kh', type: 'chi_tiet' },
  { header: HEADERS.CUSTOMER_NAME, key: 'ten_kh', type: 'chi_tiet' },
  { header: HEADERS.DEBIT_ACCOUNT, key: 'tk_no', type: 'chi_tiet' },
  { header: HEADERS.AMOUNT_NT, key: 'tien_nt', type: 'chi_tiet' },
  { header: HEADERS.AMOUNT, key: 'tien', type: 'chi_tiet' },
  { header: HEADERS.DETAIL_DESC, key: 'dien_giai', type: 'chi_tiet' },
  { header: HEADERS.DEPARTMENT, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.FEE, key: 'ma_phi', type: 'chi_tiet' },

  // Thue fields
  { header: HEADERS.MA_THUE, key: 'ma_thue', type: 'thue' },
  { header: HEADERS.TK_THUE, key: 'tk_thue_no', type: 'thue' },
  { header: HEADERS.SO_HD, key: 'so_ct0', type: 'thue' },
  { header: HEADERS.KY_HIEU, key: 'so_ct2', type: 'thue' },
  { header: HEADERS.NGAY_HD, key: 'ngay_ct0', type: 'thue' },
  { header: HEADERS.MAU_HD, key: 'ma_mau_ct', type: 'thue' },
  { header: HEADERS.MAU_BC, key: 'mau_bao_cao', type: 'thue' },
  { header: HEADERS.MA_TC, key: 'ma_tc_thue', type: 'thue' },
  { header: HEADERS.MA_NCC, key: 'ma_kh', type: 'thue' },
  { header: HEADERS.TEN_NCC, key: 'ten_kh_thue', type: 'thue' },
  { header: HEADERS.ADDRESS, key: 'dia_chi', type: 'thue' },
  { header: HEADERS.MA_SO_THUE, key: 'ma_so_thue', type: 'thue' },
  { header: HEADERS.TEN_VT_THUE, key: 'ten_vt_thue', type: 'thue' },
  { header: HEADERS.THUE_NT, key: 't_thue_nt', type: 'thue' },
  { header: HEADERS.THUE, key: 't_thue', type: 'thue' },
  { header: HEADERS.CUC_THUE, key: 'ma_kh9', type: 'thue' },
  { header: HEADERS.GHI_CHU, key: 'ghi_chu', type: 'thue' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'tk', 'tknh'],
  chi_tiet: ['ma_kh', 'tk_no', 'ma_bp', 'ma_phi'],
  thue: ['ma_thue', 'tk_thue_no', 'ma_mau_ct', 'ma_tc_thue', 'ma_kh', 'ma_kh9']
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const parseNum = (val: unknown, def = 0) => {
  if (val == null || String(val).trim() === '') return def;
  const n = parseFloat(String(val));
  return Number.isNaN(n) ? def : Math.round(n * 100) / 100;
};

const hasData = (item: Record<string, unknown>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

type ParsedSectionMap = Record<string, Record<string, unknown[]>>;

const getVal = (
  uuid: ParsedSectionMap | undefined,
  parsed: Record<string, unknown[]>,
  section: string,
  field: string,
  i: number
): string | undefined => {
  const value = uuid?.[section]?.[field]?.[i] || parsed[field]?.[i];
  return value == null || value === '' ? undefined : String(value);
};

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, unknown[]>, fields: string[]) =>
    Object.fromEntries(
      fields.map(f => {
        const arr = section[f] || [];
        const hasAnyData = arr.some((v: unknown) => v != null && v !== '');
        if (!hasAnyData) return [f, []];
        return [f, arr.map((v: unknown) => (v == null || v === '' ? '' : v))];
      })
    );

  return {
    main: build(data.main || {}, UUID_FIELDS.main),
    chi_tiet: build(data.chi_tiet || {}, UUID_FIELDS.chi_tiet),
    thue: build(data.thue || {}, UUID_FIELDS.thue)
  };
};

const ValidateUUID = (
  response: ParsedSectionMap | null | undefined,
  columnMapping: Record<string, ColumnMapping>
): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  const sectionMap = { main: 'main', chi_tiet: 'chi_tiet', thue: 'thue' } as const;

  Object.entries(sectionMap).forEach(([responseKey, configType]) => {
    const data = response[responseKey];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === configType);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section: configType,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const TransformToBodies = (
  dataRows: unknown[][],
  parsedData: ParsedExcelData,
  uuidResponse: ParsedSectionMap | undefined,
  context: { entityUnitUUID: string }
): ImportBody[] => {
  const { main = {}, chi_tiet = {}, thue = {} } = parsedData;
  const records = new Map<string, ImportBody>();
  const detailMap = new Map<string, ImportBody['chi_tiet']>();
  const thueMap = new Map<string, ImportBody['thue']>();

  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);
  const thueKeys = HEADER_CONFIG.filter(c => c.type === 'thue').map(c => c.key);

  for (let i = 0; i < dataRows.length; i++) {
    const docNum = String(main.so_ct?.[i] ?? '').trim();
    if (!docNum) continue;

    if (!records.has(docNum)) {
      const docDate = convertExcelDate(main.ngay_ct?.[i]) || format(new Date(), 'yyyy-MM-dd');
      records.set(docNum, {
        unit_id: context.entityUnitUUID,
        ma_ngv: '2',
        dia_chi: main.dia_chi?.[i] || undefined,
        ong_ba: main.ong_ba?.[i] || undefined,
        dien_giai: main.dien_giai?.[i] || undefined,
        tk: getVal(uuidResponse, main, 'main', 'tk', i) || '',
        tknh: getVal(uuidResponse, main, 'main', 'tknh', i) || undefined,
        loai_hd: main.loai_hd?.[i] || '1',
        so_ct: docNum,
        ngay_ct: docDate,
        ngay_lct: docDate,
        i_so_ct: 0,
        ma_nt: getVal(uuidResponse, main, 'main', 'ma_nt', i) || '',
        ty_gia: parseNum(main.ty_gia?.[i], 1),
        status: '5',
        t_tien_nt: 0,
        t_tien: 0,
        t_thue_nt: 0,
        t_thue: 0,
        t_cp_nt: 0,
        t_cp: 0,
        t_tt_nt: 0,
        t_tt: 0,
        ma_kh: '',
        chi_tiet: [],
        thue: []
      });
    }

    // Process Detail Row
    const details = detailMap.get(docNum) || [];
    const tien_nt = parseNum(chi_tiet.tien_nt?.[i]);
    const tien = parseNum(chi_tiet.tien?.[i]) || tien_nt;

    const detailItem = {
      line: details.length + 1,
      dien_giai: chi_tiet.dien_giai?.[i] || main.dien_giai?.[i] || undefined,
      ma_kh: getVal(uuidResponse, chi_tiet, 'chi_tiet', 'ma_kh', i),
      tk_no: getVal(uuidResponse, chi_tiet, 'chi_tiet', 'tk_no', i),
      tien_nt,
      tien,
      ma_bp: getVal(uuidResponse, chi_tiet, 'chi_tiet', 'ma_bp', i),
      ma_phi: getVal(uuidResponse, chi_tiet, 'chi_tiet', 'ma_phi', i)
    };

    if (hasData(detailItem, detailKeys)) {
      details.push(detailItem);
      detailMap.set(docNum, details);
    }

    // Process Thue Row
    const thueList = thueMap.get(docNum) || [];
    const t_thue_nt = parseNum(thue.t_thue_nt?.[i]);
    const t_thue = parseNum(thue.t_thue?.[i]) || t_thue_nt;
    const t_tien_nt = parseNum(thue.t_tien_nt?.[i]) || tien_nt;
    const t_tien = parseNum(thue.t_tien?.[i]) || tien;
    const thue_suat = parseNum(thue.thue_suat?.[i]);

    const thueItem = {
      line: thueList.length + 1,
      so_ct0: thue.so_ct0?.[i] || undefined,
      so_ct2: thue.so_ct2?.[i] || undefined,
      ngay_ct0: convertExcelDate(thue.ngay_ct0?.[i]) || undefined,
      ma_thue: getVal(uuidResponse, thue, 'thue', 'ma_thue', i),
      thue_suat,
      ma_mau_ct: getVal(uuidResponse, thue, 'thue', 'ma_mau_ct', i),
      ma_mau_bc: thue.mau_bao_cao?.[i] || '1',
      ma_tc_thue: getVal(uuidResponse, thue, 'thue', 'ma_tc_thue', i),
      ma_kh: getVal(uuidResponse, thue, 'thue', 'ma_kh', i),
      ten_kh_thue: thue.ten_kh_thue?.[i] || undefined,
      dia_chi: thue.dia_chi?.[i] || undefined,
      ma_so_thue: thue.ma_so_thue?.[i] || undefined,
      ten_vt_thue: thue.ten_vt_thue?.[i] || undefined,
      t_tien_nt,
      t_tien,
      tk_thue_no: getVal(uuidResponse, thue, 'thue', 'tk_thue_no', i),
      tk_du: getVal(uuidResponse, thue, 'thue', 'tk_du', i) || getVal(uuidResponse, main, 'main', 'tk', i),
      t_thue_nt,
      t_thue,
      ma_kh9: getVal(uuidResponse, thue, 'thue', 'ma_kh9', i),
      ghi_chu: thue.ghi_chu?.[i] || undefined
    };

    if (hasData(thueItem, thueKeys)) {
      thueList.push(thueItem);
      thueMap.set(docNum, thueList);
    }
  }

  return Array.from(records.values()).map(record => {
    const docNum = record.so_ct || '';
    record.chi_tiet = detailMap.get(docNum) || [];
    record.thue = thueMap.get(docNum) || [];

    const total_tien_nt = record.chi_tiet.reduce((a, item) => a + (item.tien_nt || 0), 0);
    const total_tien = record.chi_tiet.reduce((a, item) => a + (item.tien || 0), 0);

    const total_thue_nt = record.thue.reduce((a, item) => a + (item.t_thue_nt || 0), 0);
    const total_thue = record.thue.reduce((a, item) => a + (item.t_thue || 0), 0);

    record.t_tien_nt = Math.round(total_tien_nt * 100) / 100;
    record.t_tien = Math.round(total_tien * 100) / 100;

    record.t_thue_nt = Math.round(total_thue_nt * 100) / 100;
    record.t_thue = Math.round(total_thue * 100) / 100;

    record.t_tt_nt = Math.round((total_tien_nt + total_thue_nt) * 100) / 100;
    record.t_tt = Math.round((total_tien + total_thue) * 100) / 100;

    record.ma_kh = record.chi_tiet[0]?.ma_kh || '';

    return record;
  });
};

export interface ImportBody {
  unit_id: string;
  ma_ngv?: string;
  dia_chi?: string;
  ong_ba?: string;
  dien_giai?: string;
  tk: string;
  tknh?: string;
  loai_hd?: string;
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  i_so_ct?: number;
  ma_nt: string;
  ty_gia: number | string;
  status?: string;
  t_tien_nt: number;
  t_tien: number;
  t_thue_nt: number;
  t_thue: number;
  t_cp_nt: number;
  t_cp: number;
  t_tt_nt: number;
  t_tt: number;
  ma_kh: string;
  chi_tiet: Array<{
    line?: number;
    dien_giai?: string | null;
    ma_kh?: string | null;
    tk_no?: string | null;
    tien_nt?: number;
    tien?: number;
    ma_bp?: string | null;
    ma_phi?: string | null;
  }>;
  thue?: Array<{
    line?: number;
    so_ct0?: string;
    so_ct2?: string;
    ngay_ct0?: string;
    ma_thue?: string | null;
    thue_suat?: number;
    ma_mau_ct?: string | null;
    ma_mau_bc?: string;
    ma_tc_thue?: string | null;
    ma_kh?: string | null;
    ten_kh_thue?: string;
    dia_chi?: string;
    ma_so_thue?: string;
    ten_vt_thue?: string;
    t_tien_nt?: number;
    t_tien?: number;
    tk_thue_no?: string | null;
    tk_du?: string | null;
    t_thue_nt?: number;
    t_thue?: number;
    ma_kh9?: string | null;
    ghi_chu?: string;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
